import React from 'react';
import { IDocumentDetails, MAIL_STATUS } from '../@types/models';
import SpinnerComponent from '../common/components/controls/spinner';
import { getMultipleArchivedMailInfo, getSelectedMessageIds } from '../services/api/dataServiceOutlook';
import { useId, Toaster, useToastController, ToastIntent } from '@fluentui/react-components';
import { t } from "i18next";
import { getDocumentDetailComp, getArchiveHome } from '../common/components/home/HomeComponents';
import { getToastMessageComponent } from '../common/components/ToastMessage';
import { getEnvironment360URL } from '../services/configs/appConfig';
import { isInteractiveAuthEnabled, authenticateTo360 } from '../services/api/authService';
import { checkAuthStatus } from '../services/api/authInitializer';
import { MultiMailArchiveHome } from '../common/components/archiveHome/MultiMailArchiveHome.tsx';
import { getArchivedDocumentsForItemIds } from '../common/components/archiveHome/MultiMailArchiveHome.utils';
import { MultipleArchiveDetail } from '../common/components/archiveDetail/MultipleArchiveDetail';
import { getCurrentInternetMessageId } from '../services/api/OutlookMessageIdUtil';
import { useStyles } from './OutlookHome.styles';
import { OutlookTestButtons } from './OutlookTestButtons';
import { useOutlookTestActions } from './useOutlookTestActions';

export const OutlookHome: React.FC = () => {
    const loadingText = t("Loading...");
    const [showSpinner, setShowSpinner] = React.useState(true);
    const [errorMsg, setErrorMsg] = React.useState("");
    const [loadingStatus, setLoadingStatus] = React.useState(MAIL_STATUS.LOADING);
    const [documentInfo, setDocumentInfo] = React.useState<IDocumentDetails[]>([]);
    const [hasMultipleSelection, setHasMultipleSelection] = React.useState(false);
    const retryCheckCaseExistsRef = React.useRef<() => Promise<void>>();
    const selectionRequestIdRef = React.useRef(0);
    const processMailTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const styles = useStyles();
    const toasterId = useId("toastIdHome");
    const { dispatchToast, dismissToast } = useToastController(toasterId);
    const ignoreRetryError = React.useCallback(() => undefined, []);
    const retryCheckCaseExists = React.useCallback(() => {
        const retryCheck = retryCheckCaseExistsRef.current;
        if (retryCheck) {
            retryCheck().catch(ignoreRetryError);
        }
    }, [ignoreRetryError]);
    const showToast = React.useCallback((message: string, toastType: ToastIntent) => {
        setTimeout(() => {
            const msg = getToastMessageComponent(message, retryCheckCaseExists);
            dispatchToast(msg, { intent: toastType });
        }, 100);
    }, [dispatchToast, retryCheckCaseExists]);
    const { testAuthenticate, testPing } = useOutlookTestActions({ setShowSpinner, showToast });

    const checkCaseExists = React.useCallback(async () => {
        dismissToast(toasterId);
        const requestId = ++selectionRequestIdRef.current;
        setShowSpinner(true);
        setLoadingStatus(MAIL_STATUS.LOADING);
        setErrorMsg("");
        try {
            const selectedMessageIds = await getSelectedMessageIds();
            if (requestId !== selectionRequestIdRef.current) {
                return;
            }

            if (selectedMessageIds.length !== 1) {
                setDocumentInfo([]);
                setHasMultipleSelection(selectedMessageIds.length > 1);
                setLoadingStatus(MAIL_STATUS.NOT_EXISTS);
                setShowSpinner(false);
                return;
            }

            setHasMultipleSelection(false);
            const currentSelectedMessageId = await getCurrentInternetMessageId() || selectedMessageIds[0];
            if (!currentSelectedMessageId) {
                setShowSpinner(false);
                setErrorMsg(t("Cannot retrieve mail IDs for selected emails"));
                setLoadingStatus(MAIL_STATUS.ERROR);
                return;
            }
            void getMultipleArchivedMailInfo((resData) => {
                if (requestId !== selectionRequestIdRef.current) {
                    return;
                }

                setShowSpinner(false);
                const documents = getArchivedDocumentsForItemIds(resData, [currentSelectedMessageId]);
                if (documents.length > 0) {
                    setDocumentInfo(documents);
                    setLoadingStatus(MAIL_STATUS.EXISTS);
                } else {
                    setLoadingStatus(MAIL_STATUS.NOT_EXISTS);
                }
            }, (err: string) => {
                if (requestId !== selectionRequestIdRef.current) {
                    return;
                }

                setShowSpinner(false);
                setErrorMsg(err);
                setLoadingStatus(MAIL_STATUS.ERROR);
            }, [currentSelectedMessageId]);
        } catch (exception) {
            if (requestId !== selectionRequestIdRef.current) {
                return;
            }

            setShowSpinner(false);
            if (typeof exception === 'string') setErrorMsg(exception);
            else if (exception instanceof Error) setErrorMsg(exception.message);
            setLoadingStatus(MAIL_STATUS.ERROR);
        }
    }, [dismissToast, toasterId]);
    retryCheckCaseExistsRef.current = checkCaseExists;

    const processMail = React.useCallback(() => {
        if (processMailTimerRef.current) clearTimeout(processMailTimerRef.current);
        // Outlook can raise ItemChanged and SelectedItemsChanged for the same mail change. Adding timeout to prevent the duplicate processing.
        processMailTimerRef.current = setTimeout(() => {
            processMailTimerRef.current = null;
            void (async () => {
                const multiSaved = new URLSearchParams(globalThis.location.search).get("multiSaved") === 'true';
                if (multiSaved) {
                    setDocumentInfo([]);
                    setHasMultipleSelection(true);
                    setLoadingStatus(MAIL_STATUS.NOT_EXISTS);
                    setShowSpinner(false);
                    return;
                }

                await checkCaseExists();
            })();
        }, 50);
    }, [checkCaseExists]);

    React.useEffect(() => {
        const initializeAndLoad = async () => {
            const queryParams = new URLSearchParams(globalThis.location.search);
            const multiSaved = queryParams.get("multiSaved") === 'true';
            const p360Url = getEnvironment360URL();
            if (!p360Url) {
                setShowSpinner(false);
                setErrorMsg(t("The 360 url not set correctly"));
                setLoadingStatus(MAIL_STATUS.ERROR);
                return;
            }

            if (multiSaved) {
                setDocumentInfo([]);
                setHasMultipleSelection(true);
                setLoadingStatus(MAIL_STATUS.NOT_EXISTS);
                setShowSpinner(false);
                return;
            }

            if (isInteractiveAuthEnabled()) {
                try {
                    const isAuthenticated = await checkAuthStatus(p360Url);
                    if (!isAuthenticated) {
                        setShowSpinner(true);
                        setLoadingStatus(MAIL_STATUS.LOADING);
                        await authenticateTo360(p360Url, false);
                    }
                    await checkCaseExists();
                } catch (error) {
                    setShowSpinner(false);
                    const errorMessage = error instanceof Error ? error.message : String(error);
                    setErrorMsg(`Authentication failed: ${errorMessage}`);
                    setLoadingStatus(MAIL_STATUS.ERROR);
                }
            } else await checkCaseExists();
        };
        void initializeAndLoad();
    }, [checkCaseExists]);
    React.useEffect(() => {
        const supportsSelectedItemsChanged = Office.context.requirements?.isSetSupported?.('Mailbox', '1.13')
            && Office.EventType.SelectedItemsChanged;
        Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, processMail);
        if (supportsSelectedItemsChanged) {
            Office.context.mailbox.addHandlerAsync(Office.EventType.SelectedItemsChanged, processMail);
        }
        return () => {
            if (processMailTimerRef.current) {
                clearTimeout(processMailTimerRef.current);
                processMailTimerRef.current = null;
            }
            Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
            if (supportsSelectedItemsChanged) {
                Office.context.mailbox.removeHandlerAsync(Office.EventType.SelectedItemsChanged);
            }
        };
    }, [processMail]);
    React.useEffect(() => {
        if (loadingStatus === MAIL_STATUS.ERROR && errorMsg) {
            showToast(errorMsg, "error");
        }
    }, [loadingStatus, errorMsg, showToast]);
    let uiToDisplay: React.JSX.Element | null = null;
    if (loadingStatus === MAIL_STATUS.EXISTS && documentInfo.length === 1) uiToDisplay = getDocumentDetailComp(documentInfo[0]);
    else if (loadingStatus === MAIL_STATUS.EXISTS && documentInfo.length > 1) uiToDisplay = <MultipleArchiveDetail documents={documentInfo} />;
    else if (loadingStatus === MAIL_STATUS.NOT_EXISTS)
        uiToDisplay = hasMultipleSelection ? <MultiMailArchiveHome /> : getArchiveHome();
    return (<div className={styles.containerbody} aria-busy={showSpinner}>
        <OutlookTestButtons onAuthenticate={testAuthenticate} onPing={testPing} onCheckCase={checkCaseExists} />
        {uiToDisplay}<Toaster toasterId={toasterId} />
        <SpinnerComponent showSpinner={showSpinner} size='medium' label={loadingText} labelPosition='after' spinnerStyle={styles.loadingOverlay} /></div>);
};
export default OutlookHome;
